import { lazy } from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import { redirectTarget } from '@/utils/auth';
import { useAuthStore } from '@/store/authStore';
import Login from '@/views/Login';
import Logout from '@/views/Logout';
import DefaultLayout from '@/layouts/DefaultLayout';
import PublicLayout from '@/layouts/PublicLayout';

const SetPassword = lazy(() => import('@/views/SetPassword'));
const Dashboard = lazy(() => import('@/views/Dashboard'));
const DashboardV2 = lazy(() => import('@/views/DashboardV2'));
const ExclusionsMap = lazy(() => import('@/views/ExclusionsMap'));
const MapCells = lazy(() => import('@/views/MapCells'));
const Orders = lazy(() => import('@/views/Orders'));
const ActivitiesList = lazy(() => import('@/views/orders/ActivitiesList'));
const ActivitiesV2List = lazy(() => import('@/views/orders/ActivitiesV2List'));
const ActivityDetails = lazy(() => import('@/views/orders/ActivityDetails'));
const ActivityCompare = lazy(() => import('@/views/orders/ActivityCompare'));
const Alerts = lazy(() => import('@/views/Alerts'));
const AlertsList = lazy(() => import('@/views/alerts/AlertsList'));
const AlertsProfile = lazy(() => import('@/views/alerts/AlertsProfile'));
const Clients = lazy(() => import('@/views/Clients'));
const ClientsList = lazy(() => import('@/views/clients/ClientsList'));
const ClientsNew = lazy(() => import('@/views/clients/ClientsNew'));
const ClientsProfile = lazy(() => import('@/views/clients/ClientsProfile'));
const Challenges = lazy(() => import('@/views/Challenges'));
const ChallengesTable = lazy(() => import('@/views/challenges/ChallengesTable'));
const ChallengesNew = lazy(() => import('@/views/challenges/ChallengesNew'));
const ChallengesTableParticipants = lazy(() => import('@/views/challenges/ChallengesTableParticipants'));
const ChallengesLeaderboard = lazy(() => import('@/views/challenges/ChallengesLeaderboard'));
const Sponsors = lazy(() => import('@/views/Sponsors'));
const SponsorsTable = lazy(() => import('@/views/sponsors/SponsorsTable'));
const SponsorsNew = lazy(() => import('@/views/sponsors/SponsorsNew'));
const ShortLinks = lazy(() => import('@/views/ShortLinks'));
const ShortLinksTable = lazy(() => import('@/views/shortlinks/ShortLinksTable'));
const ShortLinkForm = lazy(() => import('@/views/shortlinks/ShortLinkForm'));
const Entities = lazy(() => import('@/views/Entities'));
const EntitiesTable = lazy(() => import('@/views/entities/EntitiesTable'));
const EntitiesNew = lazy(() => import('@/views/entities/EntitiesNew'));
const EntitiesTableSites = lazy(() => import('@/views/entities/EntitiesTableSites'));
const Reports = lazy(() => import('@/views/Reports'));
const PointsOfInterest = lazy(() => import('@/views/PointsOfInterest'));
const PoiZones = lazy(() => import('@/views/PoiZones'));
const PoiCategories = lazy(() => import('@/views/PoiCategories'));
const Languages = lazy(() => import('@/views/Languages'));
const Simulator = lazy(() => import('@/views/Simulator'));
const AccountSecurity = lazy(() => import('@/views/AccountSecurity'));
const ActivitiesV2ForceFinish = lazy(() => import('@/views/orders/ActivitiesV2ForceFinish'));
const RevenuecatReconciliation = lazy(() => import('@/views/revenuecat/RevenuecatReconciliation'));
const MetricsReport = lazy(() => import('@/views/MetricsReport'));
const SegmentsReport = lazy(() => import('@/views/SegmentsReport'));

// Route guard components
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  // Mémorise la page demandée pour y revenir après la connexion
  return token ? <>{children}</> : <Navigate to="/login" replace state={{ from: location }} />;
};

const GuestGuard = ({ children }: { children: React.ReactNode }) => {
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  return !token ? <>{children}</> : <Navigate to={redirectTarget(location.state)} replace />;
};

// Basename pour le routing
const basename = '/';

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <GuestGuard>
        <PublicLayout>
          <Login />
        </PublicLayout>
      </GuestGuard>
    ),
  },
  {
    path: '/set-password',
    element: (
      <GuestGuard>
        <PublicLayout>
          <SetPassword />
        </PublicLayout>
      </GuestGuard>
    ),
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <DefaultLayout>
            <Navigate to="/dashboard" replace />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <DashboardV2 />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/dashboard-v1',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Dashboard />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/dashboard-v2',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/kpi',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <MetricsReport />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/kpi/segments',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <SegmentsReport />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/map',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <ExclusionsMap />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/map-cells',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <MapCells />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/parcours',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Orders />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <ActivitiesList />,
      },
      {
        path: ':id',
        element: <ActivityDetails />,
      },
    ],
  },
  {
    path: '/parcours-v2',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Orders />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <ActivitiesV2List />,
      },
      {
        path: ':id',
        element: <ActivityDetails />,
      },
      {
        path: 'force-finish',
        element: <ActivitiesV2ForceFinish />,
      },
    ],
  },
  {
    path: '/parcours/compare',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <ActivityCompare />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/signalements',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Alerts />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <AlertsList />,
      },
      {
        path: ':id',
        element: <AlertsProfile />,
      },
    ],
  },
  {
    path: '/utilisateurs',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Clients />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <ClientsList />,
      },
      {
        path: 'add',
        element: <ClientsNew />,
      },
      {
        path: ':id',
        element: <ClientsProfile />,
      },
    ],
  },
  {
    path: '/challenges',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Challenges />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <ChallengesTable />,
      },
      {
        path: 'all',
        element: <ChallengesTable />,
      },
      {
        path: 'add',
        element: <ChallengesNew />,
      },
      {
        path: ':id',
        element: <ChallengesNew />,
      },
      {
        path: ':id/participants',
        element: <ChallengesTableParticipants />,
      },
      {
        path: ':id/leaderboard',
        element: <ChallengesLeaderboard />,
      },
    ],
  },
  {
    path: '/sponsors',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Sponsors />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <SponsorsTable />,
      },
      {
        path: 'all',
        element: <SponsorsTable />,
      },
      {
        path: 'add',
        element: <SponsorsNew />,
      },
      {
        path: ':id',
        element: <SponsorsNew />,
      },
    ],
  },
  {
    path: '/short-links',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <ShortLinks />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <ShortLinksTable />,
      },
      {
        path: 'all',
        element: <ShortLinksTable />,
      },
      {
        path: 'add',
        element: <ShortLinkForm />,
      },
      {
        path: ':id',
        element: <ShortLinkForm />,
      },
    ],
  },
  {
    path: '/entities',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Entities />
        </DefaultLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <EntitiesTable />,
      },
      {
        path: 'all',
        element: <EntitiesTable />,
      },
      {
        path: 'add',
        element: <EntitiesNew />,
      },
      {
        path: ':id',
        element: <EntitiesNew />,
      },
      {
        path: ':id/sites',
        element: <EntitiesTableSites />,
      },
    ],
  },
  {
    path: '/reports',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Reports />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/pois',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <PointsOfInterest />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/poi-zones',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <PoiZones />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/poi-categories',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <PoiCategories />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/languages',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Languages />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/simulator',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <Simulator />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/account/security',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <AccountSecurity />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/revenuecat',
    element: (
      <AuthGuard>
        <DefaultLayout>
          <RevenuecatReconciliation />
        </DefaultLayout>
      </AuthGuard>
    ),
  },
  {
    path: '/logout',
    element: (
      <AuthGuard>
        <Logout />
      </AuthGuard>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
], {
  basename: basename,
});

